/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Nav from "react-bootstrap/Nav";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import Helmet from "react-helmet";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  createClubCatalogItem,
  fetchClubCatalogItems,
  updateClubCatalogItem,
} from "../../api/clubApi";
import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
import EventImageWithFallback from "../../components/eventImageWithFallback/EventImageWithFallback";
import {
  CATALOG_DESCRIPTION_MAX,
  CATALOG_KIND_ADDON,
  CATALOG_KIND_PREORDER,
  CATALOG_NAME_MAX,
  fileToDataImageUrl,
  formatCatalogMoney,
  unwrapCatalogItem,
  unwrapCatalogList,
  validateCatalogItemForm,
} from "../../helpers/clubCatalog";
import useAuth from "../../hooks/useAuth";
import { clubListResponce } from "../../store/features/club/clubSlice";
import { showToast } from "../../store/features/toast/toastSlice";
import Constant from "../../util/constant";
import "./clubCatalog.css";

const EMPTY_FORM = {
  kind: CATALOG_KIND_ADDON,
  name: "",
  description: "",
  image: "",
  unitPrice: "",
  currency: "INR",
  isactive: true,
  sortOrder: "0",
};

const ClubCatalogContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { clubId: paramClubId } = useParams();
  const { isAdmin, isClubManager, managedClubId } = useAuth();
  const role = window.localStorage.getItem("role");
  const clubList = useSelector((s) => s?.club?.clubList ?? []);

  const isAdminRoute =
    (role === Constant.ROLES.ADMIN || isAdmin) && Boolean(paramClubId);
  const resolvedClubId = isAdminRoute
    ? String(paramClubId)
    : isClubManager
      ? String(managedClubId || "")
      : "";

  const forbiddenOtherClub =
    isClubManager &&
    paramClubId &&
    managedClubId &&
    String(paramClubId) !== String(managedClubId);

  const [kindTab, setKindTab] = useState(CATALOG_KIND_ADDON);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState({ items: [], addons: [], preorders: [] });
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [imageUploading, setImageUploading] = useState(false);

  useEffect(() => {
    dispatch(clubListResponce());
  }, [dispatch]);

  const clubLabel = useMemo(() => {
    const c = (clubList || []).find(
      (x) => String(x._id ?? x.id) === String(resolvedClubId)
    );
    return c?.name || c?.slug || resolvedClubId || "—";
  }, [clubList, resolvedClubId]);

  const loadCatalog = useCallback(async () => {
    if (!resolvedClubId || forbiddenOtherClub) {
      setList({ items: [], addons: [], preorders: [] });
      return;
    }
    setLoading(true);
    try {
      const res = await fetchClubCatalogItems(resolvedClubId, {
        includeInactive: true,
      });
      setList(unwrapCatalogList(res));
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message:
            typeof err === "string"
              ? err
              : err?.message || t("CATALOG_LOAD_ERROR"),
          type: "error",
        })
      );
      setList({ items: [], addons: [], preorders: [] });
    } finally {
      setLoading(false);
    }
  }, [resolvedClubId, forbiddenOtherClub, dispatch, t]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const rows =
    kindTab === CATALOG_KIND_PREORDER ? list.preorders : list.addons;

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, kind: kindTab });
    setFormErrors({});
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      kind: item.kind,
      name: item.name,
      description: item.description,
      image: item.image || "",
      unitPrice: String(item.unitPrice ?? ""),
      currency: item.currency || "INR",
      isactive: item.isactive !== false,
      sortOrder: String(item.sortOrder ?? 0),
    });
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!String(file.type || "").startsWith("image/")) {
      dispatch(
        showToast({
          show: false,
          message: t("CATALOG_IMAGE_TYPE"),
          type: "error",
        })
      );
      return;
    }
    setImageUploading(true);
    try {
      const dataUrl = await fileToDataImageUrl(file);
      setForm((prev) => ({ ...prev, image: dataUrl }));
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message: err?.message || t("CATALOG_IMAGE_FAILED"),
          type: "error",
        })
      );
    } finally {
      setImageUploading(false);
    }
  };

  const onSave = async (e) => {
    e?.preventDefault?.();
    const errors = validateCatalogItemForm(form, t);
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    const body = {
      kind: form.kind,
      name: String(form.name).trim(),
      description: String(form.description || "").trim(),
      unitPrice: Number(form.unitPrice),
      currency: String(form.currency || "INR").trim() || "INR",
      isactive: Boolean(form.isactive),
      sortOrder:
        form.sortOrder === "" || form.sortOrder == null
          ? 0
          : Number(form.sortOrder),
    };
    if (editing) {
      body.image = form.image == null ? "" : String(form.image);
    } else if (form.image) {
      body.image = String(form.image);
    }
    setSaving(true);
    try {
      if (editing) {
        const res = await updateClubCatalogItem(
          resolvedClubId,
          editing._id,
          body
        );
        unwrapCatalogItem(res);
        dispatch(
          showToast({
            show: false,
            message: res?.message || t("CATALOG_UPDATE_SUCCESS"),
            type: "success",
          })
        );
      } else {
        const res = await createClubCatalogItem(resolvedClubId, body);
        unwrapCatalogItem(res);
        dispatch(
          showToast({
            show: false,
            message: res?.message || t("CATALOG_CREATE_SUCCESS"),
            type: "success",
          })
        );
      }
      closeModal();
      loadCatalog();
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message:
            typeof err === "string"
              ? err
              : err?.message || t("CATALOG_SAVE_ERROR"),
          type: "error",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const setInactive = async (item) => {
    try {
      const res = await updateClubCatalogItem(resolvedClubId, item._id, {
        isactive: false,
      });
      dispatch(
        showToast({
          show: false,
          message: res?.message || t("CATALOG_UPDATE_SUCCESS"),
          type: "success",
        })
      );
      loadCatalog();
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message:
            typeof err === "string"
              ? err
              : err?.message || t("CATALOG_SAVE_ERROR"),
          type: "error",
        })
      );
    }
  };

  const pageTitle = isClubManager
    ? []
    : [
        { title: t("EVENTS"), path: "/events-list" },
        { title: t("CLUB_MANAGEMENT"), path: "/clubs-management" },
      ];

  if (!isAdmin && !isClubManager) {
    return (
      <div className="pageContent categoryManagement p-4">
        <p className="text-danger mb-0">{t("CATALOG_ROLE_DENIED")}</p>
      </div>
    );
  }

  if (forbiddenOtherClub) {
    return (
      <div className="pageContent categoryManagement club-staff-page-shell p-4">
        <p className="text-danger mb-0">{t("CATALOG_OTHER_CLUB_DENIED")}</p>
      </div>
    );
  }

  if (!resolvedClubId) {
    return (
      <div className="pageContent categoryManagement club-staff-page-shell p-4">
        <p className="text-danger mb-0">{t("CATALOG_NO_CLUB")}</p>
      </div>
    );
  }

  return (
    <div className="pageContent categoryManagement club-staff-page-shell club-catalog-page">
      <Helmet>
        <title>{t("CATALOG_PAGE_TITLE")} | RizzTix</title>
      </Helmet>
      <Breadcrumbs
        innerPagetitle={t("CATALOG_PAGE_TITLE")}
        breadcrumbItem={t("CATALOG_PAGE_TITLE")}
        pageTitle={pageTitle}
        BreadcrumbInner={pageTitle.length > 0}
        BreadcrumbmainTitle={true}
      />
      <Card className="mainCard mainCardWrapper club-catalog-card">
        <Card.Body>
          <div className="club-catalog-toolbar">
            <div className="club-catalog-toolbar-copy">
              <span className="club-catalog-club-label">{clubLabel}</span>
              <p className="club-catalog-help mb-0">
                {kindTab === CATALOG_KIND_PREORDER
                  ? t("CATALOG_PREORDER_HELP")
                  : t("CATALOG_ADDON_HELP")}
              </p>
            </div>
            <Button
              type="button"
              className="btn btn-primary fillButton club-catalog-add-btn"
              onClick={openCreate}
            >
              <i className="bx bxs-plus-circle me-1" />
              {t("CATALOG_ADD_ITEM")}
            </Button>
          </div>
          <Nav variant="pills" className="club-catalog-tabs">
            <Nav.Item>
              <Nav.Link
                active={kindTab === CATALOG_KIND_ADDON}
                onClick={() => setKindTab(CATALOG_KIND_ADDON)}
              >
                {t("CATALOG_TAB_ADDONS")}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={kindTab === CATALOG_KIND_PREORDER}
                onClick={() => setKindTab(CATALOG_KIND_PREORDER)}
              >
                {t("CATALOG_TAB_PREORDER")}
              </Nav.Link>
            </Nav.Item>
          </Nav>
          {loading ? (
            <div className="club-catalog-loading">
              <Spinner animation="border" size="sm" className="me-2" />
              {t("LOADING")}
            </div>
          ) : rows.length === 0 ? (
            <div className="club-catalog-empty">
              <div className="club-catalog-empty-icon" aria-hidden>
                <i className="bx bx-package" />
              </div>
              <strong>{t("CATALOG_EMPTY_LIST")}</strong>
              <p>{t("CATALOG_EMPTY_PICKER")}</p>
              <Button
                type="button"
                className="btn btn-primary fillButton"
                onClick={openCreate}
              >
                <i className="bx bxs-plus-circle me-1" />
                {t("CATALOG_ADD_ITEM")}
              </Button>
            </div>
          ) : (
            <>
              <div className="table-responsive club-catalog-desktop-table">
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>{t("CATALOG_COL_ITEM")}</th>
                    <th>{t("CATALOG_COL_PRICE")}</th>
                    <th>{t("CATALOG_COL_SORT")}</th>
                    <th>{t("CATALOG_COL_STATUS")}</th>
                    <th>{t("ACTIONS")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <div className="d-flex gap-2 align-items-center">
                          {item.image ? (
                            <EventImageWithFallback
                              src={item.image}
                              alt={item.name}
                              className="club-catalog-thumb"
                            />
                          ) : (
                            <span className="club-catalog-thumb club-catalog-thumb--ph">
                              <i className="bx bx-image" />
                            </span>
                          )}
                          <div>
                            <div className="club-catalog-item-name">{item.name}</div>
                            {item.description ? (
                              <div className="text-muted club-catalog-item-desc">
                                {item.description}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td>{formatCatalogMoney(item.unitPrice, item.currency)}</td>
                      <td>{item.sortOrder}</td>
                      <td>
                        <Badge bg={item.isactive ? "success" : "secondary"}>
                          {item.isactive
                            ? t("CLUB_STATUS_ACTIVE")
                            : t("CLUB_STATUS_INACTIVE")}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="editButtton"
                            title={t("EDIT")}
                            onClick={() => openEdit(item)}
                          >
                            <i className="mdi mdi-pencil-outline font-size-24" />
                          </button>
                          {item.isactive ? (
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => setInactive(item)}
                            >
                              {t("CATALOG_DEACTIVATE")}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>
              <div className="club-catalog-mobile-list">
                {rows.map((item) => (
                  <article key={item._id} className="club-catalog-mobile-card">
                    <div className="club-catalog-mobile-card-top">
                      {item.image ? (
                        <EventImageWithFallback
                          src={item.image}
                          alt={item.name}
                          className="club-catalog-thumb"
                        />
                      ) : (
                        <span className="club-catalog-thumb club-catalog-thumb--ph">
                          <i className="bx bx-image" />
                        </span>
                      )}
                      <div className="club-catalog-mobile-card-copy">
                        <div className="club-catalog-item-name">{item.name}</div>
                        {item.description ? (
                          <div className="text-muted club-catalog-item-desc">
                            {item.description}
                          </div>
                        ) : null}
                        <div className="club-catalog-mobile-meta">
                          <span>{formatCatalogMoney(item.unitPrice, item.currency)}</span>
                          <Badge bg={item.isactive ? "success" : "secondary"}>
                            {item.isactive
                              ? t("CLUB_STATUS_ACTIVE")
                              : t("CLUB_STATUS_INACTIVE")}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="club-catalog-mobile-card-actions">
                      <Button
                        type="button"
                        variant="primary"
                        className="fillButton"
                        onClick={() => openEdit(item)}
                      >
                        {t("EDIT")}
                      </Button>
                      {item.isactive ? (
                        <Button
                          type="button"
                          variant="outline-secondary"
                          onClick={() => setInactive(item)}
                        >
                          {t("CATALOG_DEACTIVATE")}
                        </Button>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        fullscreen="sm-down"
        className="club-catalog-item-modal"
      >
        <Form onSubmit={onSave}>
          <Modal.Header className="club-catalog-modal-header">
            <Modal.Title>
              {editing ? t("CATALOG_EDIT_TITLE") : t("CATALOG_CREATE_TITLE")}
            </Modal.Title>
            <button
              type="button"
              className="club-catalog-modal-close"
              aria-label={t("CANCEL")}
              onClick={closeModal}
            >
              <i className="bx bx-x" aria-hidden="true" />
            </button>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small mb-3">
              {form.kind === CATALOG_KIND_PREORDER
                ? t("CATALOG_TAB_PREORDER")
                : t("CATALOG_TAB_ADDONS")}
            </p>
            <Form.Group className="mb-3">
              <Form.Label>{t("CATALOG_NAME")}</Form.Label>
              <Form.Control
                value={form.name}
                maxLength={CATALOG_NAME_MAX}
                placeholder={t("CATALOG_NAME")}
                isInvalid={!!formErrors.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("CATALOG_DESCRIPTION")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={CATALOG_DESCRIPTION_MAX}
                value={form.description}
                placeholder={t("CATALOG_DESCRIPTION")}
                isInvalid={!!formErrors.description}
                onChange={(e) =>
                  setForm((p) => ({ ...p, description: e.target.value }))
                }
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.description}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("CATALOG_IMAGE")}</Form.Label>
              <Form.Control
                className="mb-2"
                placeholder={t("CATALOG_IMAGE_URL_PH")}
                value={
                  String(form.image || "").startsWith("data:image/")
                    ? ""
                    : form.image
                }
                onChange={(e) =>
                  setForm((p) => ({ ...p, image: e.target.value }))
                }
              />
              <Form.Control
                type="file"
                accept="image/*"
                disabled={imageUploading}
                onChange={onPickImage}
              />
              {form.image ? (
                <div className="mt-2 d-flex gap-2 align-items-center">
                  <EventImageWithFallback
                    src={form.image}
                    alt=""
                    className="club-catalog-thumb"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setForm((p) => ({ ...p, image: "" }))}
                  >
                    {t("CATALOG_CLEAR_IMAGE")}
                  </Button>
                </div>
              ) : null}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("CATALOG_UNIT_PRICE")}</Form.Label>
              <Form.Control
                type="number"
                min={0}
                step="0.01"
                value={form.unitPrice}
                placeholder={t("CATALOG_UNIT_PRICE")}
                isInvalid={!!formErrors.unitPrice}
                onChange={(e) =>
                  setForm((p) => ({ ...p, unitPrice: e.target.value }))
                }
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.unitPrice}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("CATALOG_SORT")}</Form.Label>
              <Form.Control
                type="number"
                min={0}
                max={9999}
                value={form.sortOrder}
                placeholder={t("CATALOG_SORT")}
                isInvalid={!!formErrors.sortOrder}
                onChange={(e) =>
                  setForm((p) => ({ ...p, sortOrder: e.target.value }))
                }
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.sortOrder}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Check
              type="switch"
              id="catalog-isactive"
              label={t("CLUB_ACTIVE")}
              checked={form.isactive !== false}
              onChange={(e) =>
                setForm((p) => ({ ...p, isactive: e.target.checked }))
              }
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={closeModal}>
              {t("CANCEL")}
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? t("LOADING") : t("SAVE")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ClubCatalogContainer;
